// src/components/persons/PersonLocations/PersonLocationsPanel.jsx
import React, { useState, useMemo } from 'react';
import {
    Box,
    Stack,
    Typography,
    Button,
    Alert,
    Divider,
    Skeleton,
} from '@mui/material';
import { AddLocationAlt as AddIcon } from '@mui/icons-material';
import { PersonLocationCard } from './PersonLocationCard';
import { PersonLocationForm } from './PersonLocationForm';
import { usePersonForm } from '../../../hooks/usePersons'; // ✅ Usar el hook oficial

export const PersonLocationsPanel = ({ personId, locations = [], onNotify }) => {
    const [formMode, setFormMode] = useState(null);

    // ✅ Usar el hook oficial que ya tiene todas las mutations
    const {
        createLocationFullMutation,
        updateLocationMutation,
        deleteLocationMutation,
        isBusy
    } = usePersonForm(personId);

    const sortedLocations = useMemo(() => {
        return [...locations].sort((a, b) => {
            if (a.perlocpri && !b.perlocpri) return -1;
            if (!a.perlocpri && b.perlocpri) return 1;
            return 0;
        });
    }, [locations]);

    const handleOpenCreate = () => {
        setFormMode('create');
    };

    const handleOpenEdit = (location) => {
        setFormMode({ locId: location.locid, data: location });
    };

    const handleCloseForm = () => setFormMode(null);

    const handleSubmit = async (payload) => {
        try {
            if (formMode === 'create') {
                await createLocationFullMutation.mutateAsync({
                    personId: personId,
                    data: payload
                });
                onNotify?.('Dirección creada correctamente.', 'success');
            } else if (formMode?.locId) {
                const locId = payload.locId || formMode.locId;
                await updateLocationMutation.mutateAsync({
                    personId: personId,
                    locId: locId,
                    data: payload,
                });
                onNotify?.('Dirección actualizada correctamente.', 'success');
            }
            handleCloseForm();
        } catch (error) {
            console.error('Error:', error);
            onNotify?.(
                error?.response?.data?.error || 'Error al guardar dirección.',
                'error'
            );
        }
    };

    const handleDelete = async (location) => {
        if (window.confirm(`¿Eliminar la dirección "${location.locnom}"?`)) {
            try {
                await deleteLocationMutation.mutateAsync({
                    personId: personId,
                    perlocid: location.locid,  // ✅ Atención: el API espera perlocid
                });
                onNotify?.('Dirección eliminada correctamente.', 'success');
            } catch (error) {
                console.error('Error:', error);
                onNotify?.(
                    error?.response?.data?.error || 'Error al eliminar dirección.',
                    'error'
                );
            }
        }
    };

    return (
        <Box>
            <Stack spacing={2}>
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                            Direcciones
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {locations.length} ubicación(es) registrada(s)
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        disabled={isBusy}
                    >
                        Agregar
                    </Button>
                </Stack>

                <Divider />

                {/* Empty state */}
                {!isBusy && locations.length === 0 && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Esta persona aún no tiene direcciones registradas para el servicio de taxi.
                    </Alert>
                )}

                {/* Loading skeletons */}
                {isBusy && locations.length === 0 && (
                    <Stack spacing={1.5}>
                        {[1, 2].map((i) => (
                            <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: 2 }} />
                        ))}
                    </Stack>
                )}

                {/* Locations list */}
                <Stack spacing={1.5}>
                    {sortedLocations.map((location) => (
                        <PersonLocationCard
                            key={location.locid}
                            location={location}
                            onEdit={handleOpenEdit}
                            onDelete={handleDelete}
                            disabled={isBusy}
                        />
                    ))}
                </Stack>
            </Stack>

            {/* Form modal */}
            {(formMode === 'create' || formMode?.locId) && (
                <PersonLocationForm
                    open={true}
                    onClose={handleCloseForm}
                    onSubmit={handleSubmit}
                    defaultValues={formMode === 'create' ? null : formMode.data}
                    isSubmitting={isBusy}
                />
            )}
        </Box>
    );
};